<script lang="ts">
  /**
   * Outbox view (spec §15): the scheduled/queued sends with `sendsAt {t}` and a cancel button that
   * reopens the draft in compose (`store.cancelScheduled`, toasts `sendCancelled` / `alreadySent`).
   * Items may carry a `state` the type does not declare yet; it is shown when present.
   */
  import { untrack } from 'svelte';
  import { formatDateTime, t } from '$lib/i18n';
  import { store } from '$lib/store';
  import type { PendingSend } from '$lib/types';
  import { outboxStateLabel } from '../settings/_local';

  let busy = $state<string | null>(null);

  $effect(() => {
    untrack(() => {
      void store.loadOutbox();
    });
  });

  function stateOf(p: PendingSend): string | null {
    const s = (p as PendingSend & { state?: unknown }).state;
    return typeof s === 'string' && s !== '' ? s : null;
  }

  function isoOf(ms: number): string | undefined {
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }

  /** `retrySend` is not part of the store yet; the retry button appears only once it is. */
  function hasRetry(): boolean {
    return typeof (store as unknown as { retrySend?: unknown }).retrySend === 'function';
  }

  function retry(id: string): void {
    void (store as unknown as { retrySend: (id: string) => unknown }).retrySend(id);
  }

  async function cancel(id: string): Promise<void> {
    if (busy) return;
    busy = id;
    try {
      await store.cancelScheduled(id);
    } finally {
      busy = null;
    }
  }
</script>

<div class="outbox-view">
  <h2>{t('outbox')}</h2>
  {#if !store.pendingSends.length}
    <p class="empty-state" data-kind="outbox-empty">{t('outboxEmpty')}</p>
  {:else}
    <ul class="olist">
      {#each store.pendingSends as p (p.id)}
        {@const st = stateOf(p)}
        <li class="oitem" data-id={p.id} data-state={st}>
          <div class="ohead">
            <bdi class="osubj">{p.subject || t('noSubject')}</bdi>
            <span class="oto" dir="ltr">{p.to}</span>
          </div>
          <time class="oat" datetime={isoOf(p.sendAt)}>{t('sendsAt', { t: formatDateTime(p.sendAt) })}</time>
          {#if st}
            <span class="ostate">
              {outboxStateLabel(st)}
              {#if st === 'failed' && hasRetry()}
                <button type="button" class="ocancel oretry" onclick={() => retry(p.id)}>{t('retry')}</button>
              {/if}
            </span>
          {/if}
          <button type="button" class="ocancel" disabled={busy === p.id} onclick={() => void cancel(p.id)}>{t('cancelSchedule')}</button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  /* ===== outbox rules from the App-shell group (svelte-1n46o8q) — copied verbatim ===== */
  .outbox-view {
    padding: var(--space-5);
    overflow: auto
  }

  .outbox-view h2 {
    font-size: var(--text-lg);
    margin-bottom: var(--space-4)
  }

  .olist {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-3)
  }

  .oitem {
    display: grid;
    gap: var(--space-2);
    padding: var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-card)
  }

  .ohead {
    display: flex;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap
  }

  .osubj {
    font-weight: var(--weight-medium)
  }

  .oto,
  .oat {
    color: var(--text-muted);
    font-size: var(--text-sm)
  }

  .ocancel {
    justify-self: start;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-pill);
    background: var(--surface-subtle);
    color: var(--text-body);
    padding: var(--space-1) var(--space-4);
    min-block-size: var(--control-sm);
    font: inherit;
    cursor: pointer
  }

  .ocancel:active {
    background: var(--mail-pressed)
  }

  .empty-state {
    display: grid;
    gap: var(--space-3);
    justify-items: center;
    max-width: 380px;
    margin-inline: auto;
    padding: var(--space-16) var(--space-4);
    text-align: center;
    color: var(--text-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-body)
  }

  /* ===== appended global fixes (2026-09-04) that target the outbox ===== */
  @media (pointer:fine) {
    .outbox-view {
      scrollbar-gutter: stable
    }
  }

  /* ===== new in the rewrite: optional item state (no legacy equivalent) ===== */
  .ostate {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    color: var(--text-muted);
    font-size: var(--text-sm)
  }

  .oitem[data-state=failed] .ostate {
    color: var(--danger-500)
  }

  .ocancel:disabled {
    opacity: .42;
    cursor: default
  }
</style>
